import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useHyperlinkSelection } from 'components/WorkspaceEditor/features/hyperlinkSelection'
import { findNodeById } from 'components/WorkspaceEditor/features/promoteDemote/utils'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles/useTreeViewStyles'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { useMemo } from 'react'
import { NodeOrder } from './NodeOrder'
import { PlainText } from './PlainText/PlainText'
import { SelectionOption } from './SelectionOption'

interface HyperlinkProps {
    contentItem: Partial<TextContentElement>
    isKeyedHyperlink: boolean
    itemOrder?: string
    inactive?: boolean
    disabled?: boolean
    editable?: boolean
    disableCursorOnly?: boolean
}

export const Hyperlink = React.memo<HyperlinkProps>(
    ({
        contentItem,
        isKeyedHyperlink,
        itemOrder,
        inactive = false,
        disabled = false,
        editable = false,
        disableCursorOnly = false,
    }) => {
        const classNames = useTreeViewStyles()

        const { contentItemViewNodes } = useShallowSelector((state) => ({
            contentItemViewNodes: state.contentItemViewNodes,
        }))

        // Find the contentItemViewNode that contains this hyperlink
        const parentNode = useMemo(
            () =>
                findNodeById(contentItemViewNodes, contentItem?.id ?? '') ||
                null,
            [contentItemViewNodes, contentItem?.id]
        )

        // Use the hyperlink selection hook
        const {
            handleSelectionOptionClick,
            isSelected,
        } = useHyperlinkSelection({
            parentNode,
        })

        // Create a conditional click handler that prevents clicks when disableCursorOnly is true
        const conditionalHandleClick = React.useCallback(
            (
                currentSelectedItem: Partial<TextContentElement>,
                currentlySelected: boolean,
            ) => {
                if (disableCursorOnly) {
                    return
                }
                handleSelectionOptionClick(
                    currentSelectedItem,
                    currentlySelected
                )
            },
            [disableCursorOnly, handleSelectionOptionClick]
        )

        return (
            <>
                {isKeyedHyperlink ? (
                    <div
                        data-testid="option-item"
                        style={{ display: 'inline' }}
                    >
                        <NodeOrder itemOrder={itemOrder || ''} />
                        <PlainText
                            contentItem={contentItem}
                            classes={classNames.paragraphTypography}
                            inactive={inactive}
                            editable={editable}
                        />
                    </div>
                ) : (
                    <div className="non-keyed-hyperlink-wrapper">
                        <SelectionOption
                            contentItem={{
                                ...contentItem,
                                // Set optionItemState based on isPillSelected since API always returns null
                                optionItemState: isSelected
                                    ? 'OptedIn'
                                    : null,
                            }}
                            inactive={inactive}
                            disabled={disabled}
                            editable={editable}
                            onSelectionOptionClick={
                                conditionalHandleClick
                            }
                            disableCursorOnly={disableCursorOnly}
                        />
                    </div>
                )}
            </>
        )
    }
)
